import { BackArrow } from '@ahamove/icons';

import { Recipient } from '@ahamove/icons/order';
import type {
  ItemDescription,
  ItemTrend,
  Path,
  SpecialRequest,
} from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { useLocationStore } from '@ahm/react-map/store';
import { FloatingLabelInput } from '@ahm/react-ui';
import {
  Stack,
  Tabs,
  Group,
  ActionIcon,
  Title,
  Space,
  Box,
  Text,
  Divider,
  Button,
  Chip,
  Skeleton,
  LoadingOverlay,
  ScrollAreaAutosize,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { ContextModalProps } from '@mantine/modals';
import { PlusIcon, Search, XIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import React, { useState, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useGetItemTrend } from '@/features/booking/api/special-request';
import { useGetSuggestionItemTrend } from '@/features/booking/api/special-request/use-get-suggestion-trend';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

type InsuranceItemModalProps = ContextModalProps<{
  state: string;
  request: SpecialRequest;
  pathIndex: number;
  initialPath: Path[];
  pathItemDescription: ItemDescription[];
  itemDescription: string[];
}>;

const InsuranceItemModal = ({
  innerProps,
  context,
}: InsuranceItemModalProps) => {
  const { state, initialPath } = innerProps;
  const isMobile = useIsMobile();
  const [stateTab, setStateTab] = useState<string | null>(
    state || 'state-introduce'
  );
  const { data: trends, isLoading: isLoadingFetchItemTrend } =
    useGetItemTrend();
  const [trendItemAddmore, setTrendItemAddmore] = useState<ItemTrend>();
  const [pathIndex, setPathIndex] = useState<number>(1);
  const form = useForm({
    defaultValues: {
      path: initialPath && initialPath.length > 0 ? initialPath : [],
    },
  });
  const trendChips = useMemo(() => {
    if (!trends || trends.length === 0) {
      return [];
    } else {
      if (trendItemAddmore) {
        const existingItem = trends.find(
          (item) => item.keyword === trendItemAddmore.keyword
        );
        if (!existingItem) {
          trends.push(trendItemAddmore);
        }
      }
      return trends;
    }
  }, [trendItemAddmore, trends]);
  const handleChangeTags = useCallback(
    (pathIndex: number, values: ItemDescription[]) => {
      if (!form.getValues(`path.${pathIndex}`)) return;
      const currentDescriptions =
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        form.getValues(`path.${pathIndex}.item_descriptions`) || [];

      const isItemExist = (item: ItemDescription, array: ItemDescription[]) =>
        array.some((existingItem) => existingItem.code === item.code);
      const newItems = values.filter(
        (item) => !isItemExist(item, currentDescriptions)
      );

      if (newItems.length > 0) {
        const updatedDescriptions = [...currentDescriptions, ...newItems];
        form.setValue(
          `path.${pathIndex}.item_descriptions`,
          updatedDescriptions
        );
      } else if (values.length === 0 && currentDescriptions.length > 0) {
        form.setValue(`path.${pathIndex}.item_descriptions`, []);
      }
    },
    [form]
  );
  const handleSelectItem = (
    items: string[],
    indexPath: number,
    itemSuggestion?: ItemTrend
  ) => {
    const filteredTrendChips = trendChips.filter((trendChip) =>
      items.includes(trendChip.keyword)
    );
    if (itemSuggestion) {
      setTrendItemAddmore(itemSuggestion);
      filteredTrendChips.push(itemSuggestion);
    }

    const selectedTrendData = filteredTrendChips.map(
      ({ code, group, keyword }) => ({
        code,
        keyword,
        group,
      })
    );

    handleChangeTags(indexPath, selectedTrendData);
  };
  const handleRemoveItem = useCallback(
    (pathIndex: number, itemCode: string) => {
      const currentDescriptions =
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        form.getValues(`path.${pathIndex}.item_descriptions`) || [];
      const updatedDescriptions = currentDescriptions.filter(
        (item) => item.keyword !== itemCode
      );
      form.setValue(`path.${pathIndex}.item_descriptions`, updatedDescriptions);
    },
    [form]
  );
  return (
    <Stack
      justify="space-between"
      gap={0}
      styles={{
        root: {
          height: '100%',
          flex: '1 1 auto',
        },
      }}
    >
      <ModalHeader
        stateTab={stateTab}
        context={context}
        isMobile={isMobile}
        setStateTab={setStateTab}
      />
      <Tabs
        styles={{
          root: {
            height: '100%',
          },
        }}
        variant="pills"
        value={stateTab}
        onChange={setStateTab}
      >
        <Tabs.Panel
          value="state-path-info"
          styles={{
            panel: {
              height: '100%',
            },
          }}
        >
          <PathInfoPanel
            form={form}
            context={context}
            handleRemoveItem={handleRemoveItem}
            initialPath={initialPath}
            isMobile={isMobile}
            setStateTab={setStateTab}
            setPathIndex={setPathIndex}
            selectedChips={form.getValues(`path`)}
            // index={pathIndex}
          />
        </Tabs.Panel>
        <Tabs.Panel
          value="item-info"
          key={pathIndex}
          pt={isMobile ? 45 : 0}
          styles={{
            panel: {
              height: '100%',
            },
          }}
        >
          <ItemInfoPanel
            handleRemoveItem={handleRemoveItem}
            form={form}
            handleSelectItem={handleSelectItem}
            pathIndex={pathIndex}
            setStateTab={setStateTab}
            trends={trendChips}
            isLoadingFetchItemTrend={isLoadingFetchItemTrend}
          />
        </Tabs.Panel>
        <Tabs.Panel
          value="item-type"
          key={pathIndex}
          pt={isMobile ? 45 : 0}
          styles={{
            panel: {
              height: '100%',
            },
          }}
        >
          <AddPackageType
            form={form}
            setStateTab={setStateTab}
            pathIndex={pathIndex}
            handleSelectItem={handleSelectItem}
            trends={trends}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

const ModalHeader = ({ stateTab, context, isMobile, setStateTab }) => {
  if (stateTab === 'state-introduce') return;
  if (stateTab === 'item-info' || stateTab === 'item-type') {
    return (
      <ItemInfoHeader
        stateTab={stateTab}
        context={context}
        isMobile={isMobile}
        setStateTab={setStateTab}
      />
    );
  }
  return <DefaultHeader context={context} isMobile={isMobile} />;
};

const ItemInfoHeader = ({ stateTab, context, isMobile, setStateTab }) => {
  const handleBack = () => {
    if (stateTab === 'item-type') {
      setStateTab('item-info');
    } else if (stateTab === 'item-info') {
      setStateTab('state-path-info');
    } else {
      context.closeAll();
    }
  };

  return (
    <HeaderWrapper isMobile={isMobile}>
      {renderBackButton(isMobile, handleBack)}
      <HeaderTitle stateTab={stateTab} />
      {renderSpacerOrCloseButton(isMobile, handleBack)}
    </HeaderWrapper>
  );
};

const DefaultHeader = ({ context, isMobile }) => {
  const tBooking = useTranslations('booking');

  return (
    <HeaderWrapper isMobile={isMobile}>
      <ActionIcon
        style={{ outline: 'none !important' }}
        size={44}
        variant="transparent"
        c="neutral"
        onClick={() => context.closeAll()}
      >
        <XIcon size={24} />
      </ActionIcon>
      <Title pl={16} order={4}>
        {tBooking('special_requests.package_info_title')}
      </Title>
      {isMobile && <Space w={44} />}
    </HeaderWrapper>
  );
};

const HeaderWrapper = ({ children, isMobile }) => (
  <Group
    justify="space-between"
    w="100%"
    bg="white"
    pos={isMobile ? 'fixed' : 'sticky'}
    top={0}
    right={0}
    left={0}
    style={(theme) => ({
      borderBottom: `1px solid ${theme.colors.neutral[0]}`,
      zIndex: 10,
      flexDirection: isMobile ? 'row' : 'row-reverse',
    })}
  >
    {children}
  </Group>
);

const HeaderTitle = ({ stateTab }) => (
  <Title order={4}>
    {stateTab === 'item-info' ? 'Thông tin hàng hoá' : 'Thêm loại hàng hoá'}
  </Title>
);

const renderBackButton = (isMobile, handleBack) =>
  isMobile ? (
    <ActionIcon
      style={{ outline: 'none !important' }}
      size={44}
      variant="transparent"
      c="neutral"
      onClick={handleBack}
    >
      <BackArrow size={24} height={24} width={24} />
    </ActionIcon>
  ) : (
    <Space w={44} />
  );

const renderSpacerOrCloseButton = (isMobile, handleBack) =>
  isMobile ? (
    <Space w={44} />
  ) : (
    <ActionIcon
      style={{ outline: 'none !important' }}
      size={44}
      c="neutral"
      variant="transparent"
      onClick={handleBack}
    >
      <BackArrow size={24} height={24} width={24} />
    </ActionIcon>
  );

const PathInfoPanel = ({
  context,
  handleRemoveItem,
  form,
  initialPath,
  isMobile,
  setStateTab,
  setPathIndex,
  selectedChips,
  // index,
}) => {
  const setPaths = useLocationStore.use.setPaths();
  const tBooking = useTranslations('common');
  const onSubmit = (values) => {
    setPaths(values.path);
    context.closeAll();
    // handleChangeRequestParams(request._id, true);
    // deleteCookie(COOKIE_NAME__TRANSIT_INSURANCE_SPECIAL_REQUEST_UNCHECK, {
    //   domain: process.env.NEXT_PUBLIC_DOMAIN,
    //   path: '/',
    // });
  };
  return (
    <form
      style={{
        height: '100%',
        paddingTop: isMobile ? 46 : 0,
      }}
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          justifyContent: 'space-between',
          flex: '1 1 auto',
          width: '100%',
          height: '100%',
          backgroundColor: '#fff',
        }}
      >
        <Stack
          pb={24}
          style={{
            backgroundColor: '#fff',
            overflowY: 'auto',
            maxHeight: isMobile ? '100%' : 450,
            width: '100%',
            minHeight: 268,
          }}
          gap={0}
        >
          {initialPath && initialPath.length > 0
            ? initialPath
                .slice(1)
                .map((path, i) => (
                  <PathItem
                    key={i}
                    handleRemoveItem={handleRemoveItem}
                    path={path}
                    index={i + 1}
                    form={form}
                    setStateTab={setStateTab}
                    setPathIndex={setPathIndex}
                    chipsForThisPath={selectedChips}
                  />
                ))
            : null}
        </Stack>
        <Box p={16}>
          <Button fullWidth type="submit" size="md">
            {tBooking('actions.confirm')}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

const PathItem = ({
  handleRemoveItem,
  path,
  index,
  form,
  setStateTab,
  setPathIndex,
  chipsForThisPath,
}) => {
  const tBooking = useTranslations('booking');
  return (
    <Group wrap="nowrap" w="100%" align="flex-start" p={16}>
      <ActionIcon variant="transparent" ml={-6} mt={-4}>
        <Recipient className="size-6" />
      </ActionIcon>
      <Stack gap={8} w="100%">
        <Text lineClamp={1} fz={14}>
          {path.address}
        </Text>
        <Chip.Group
          multiple={true}
          {...form.getValues(`path.${index + 1}.item_descriptions`)}
        >
          <Group justify="flex-start" w="100%" gap={12}>
            {chipsForThisPath[index].item_descriptions?.map(
              (item: ItemDescription, i: number) => (
                <Chip
                  styles={{
                    root: {
                      cursor: 'default',
                      pointerEvents: 'none',
                      opacity: 1,
                    },
                    input: {
                      cursor: 'default',
                      pointerEvents: 'none',
                    },
                    label: {
                      cursor: 'default',
                      pointerEvents: 'none',
                    },
                  }}
                  readOnly
                  value={item.code}
                  key={item.code + item.keyword + i}
                  variant="outline"
                >
                  <Group
                    gap={1}
                    justify="space-between"
                    align="center"
                    wrap="nowrap"
                    style={{ width: '100%' }}
                  >
                    {item.keyword}
                    <ActionIcon
                      w={16}
                      h={16}
                      variant="transparent"
                      mr={-8}
                      color="neutral"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveItem(index, item.keyword);
                      }}
                      style={{
                        zIndex: 10,
                        pointerEvents: 'auto',
                      }}
                    >
                      <XIcon size={16} />
                    </ActionIcon>
                  </Group>
                </Chip>
              )
            )}
          </Group>
        </Chip.Group>
        <Button
          rightSection={<PlusIcon size={24} />}
          size="compact-sm"
          ml={-8}
          styles={{
            root: {
              width: 'fit-content',
            },
            label: {
              lineHeight: '20px',
              fontWeight: 700,
              fontSize: 14,
            },
            inner: {
              justifyContent: 'flex-start',
            },
          }}
          variant="subtle"
          onClick={() => {
            setStateTab('item-info');
            setPathIndex(index);
          }}
        >
          {tBooking('special_requests.add_info_package_type_into_path')}
        </Button>
      </Stack>
    </Group>
  );
};

const ItemInfoPanel = ({
  handleRemoveItem,
  form,
  handleSelectItem,
  pathIndex,
  setStateTab,
  trends,
  isLoadingFetchItemTrend,
}) => {
  const tBooking = useTranslations('booking');
  const tBookingActions = useTranslations('common');
  const isMobile = useIsMobile();
  // const [keyWord, setKeyword] = useState('');
  // const [debounced] = useDebouncedValue(keyWord, 500);
  const [trendItemAddmore] = useState<ItemTrend>();

  const trendChips = useMemo(() => {
    if (!trends || trends.length === 0) {
      return [];
    } else {
      if (trendItemAddmore) {
        const existingItem = trends.find(
          (item) => item.keyword === trendItemAddmore.keyword
        );
        if (!existingItem) {
          trends.push(trendItemAddmore);
        }
      }
      return trends;
    }
  }, [trendItemAddmore, trends]);

  return (
    <Box
      key={pathIndex}
      style={{
        height: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
        gap: 0,
        flex: '1 1 auto',
        backgroundColor: '#fff',
        transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
      }}
    >
      <Stack
        pb={24}
        gap={0}
        style={{
          backgroundColor: '#fff',
        }}
      >
        <ScrollAreaAutosize
          type="hover"
          styles={{
            root: {
              width: '100%',
              maxHeight: isMobile ? '100%' : 520,
            },
          }}
        >
          <Text mt={10} mx={24} mb={24}>
            Vui lòng thêm loại hàng hoá để được bồi thường ở mức tối đa
          </Text>
          <Divider mb={16} mx={24} />
          <Text mb={16} tt="uppercase" mx={24}>
            {tBooking('special_requests.recomend_package_info_type_for_you')}
          </Text>

          <Box mx={24}>
            <Chip.Group
              multiple={true}
              onChange={(e) => {
                if (
                  form
                    .watch(`path.${pathIndex}.item_descriptions`)
                    ?.map(({ keyword }) => keyword)
                    ?.includes(e)
                ) {
                  return;
                }
                handleSelectItem(e, pathIndex);
              }}
            >
              <Group justify="flex-start" w="100%" gap={12}>
                {isLoadingFetchItemTrend && (
                  <Group>
                    {Array.from({ length: 8 }).map((_, index) => (
                      <Skeleton w={110} h={28} radius={14} key={index} />
                    ))}
                  </Group>
                )}

                {trendChips?.map((trend, i) => (
                  <Chip
                    variant="outline"
                    checked={form
                      .watch(`path.${pathIndex}.item_descriptions`)
                      ?.map(({ keyword }) => keyword)
                      ?.includes(trend.keyword)}
                    size="sm"
                    value={trend.keyword}
                    key={trend.keyword + trend.code + i}
                    styles={{
                      label: {
                        fontWeight: 500,
                        fontSize: 14,
                        padding: '4px 8px',
                      },
                      iconWrapper: {
                        display: 'none',
                      },
                    }}
                  >
                    <Group
                      gap={1}
                      justify="space-between"
                      align="center"
                      wrap="nowrap"
                      style={{ width: '100%' }}
                    >
                      {trend.keyword}
                      {form
                        .watch(`path.${pathIndex}.item_descriptions`)
                        ?.map(({ keyword }) => keyword)
                        ?.includes(trend.keyword) && (
                        <ActionIcon
                          w={16}
                          h={16}
                          variant="transparent"
                          mr={-8}
                          color="neutral"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleRemoveItem(pathIndex, trend.keyword);
                          }}
                        >
                          <XIcon size={16} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Chip>
                ))}
              </Group>
            </Chip.Group>
          </Box>

          <Divider my={16} />

          <Button
            mx={16}
            rightSection={<PlusIcon size={24} />}
            size="compact-sm"
            styles={{
              root: {
                width: 'fit-content',
              },
              inner: {
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
              },
              label: {
                lineHeight: '20px',
                fontWeight: 700,
                fontSize: 14,
              },
            }}
            variant="subtle"
            onClick={() => {
              setStateTab('item-type');
            }}
          >
            {tBooking('special_requests.add_info_package_type')}
          </Button>
        </ScrollAreaAutosize>
      </Stack>
      <Box
        mt={24}
        px={24}
        pb={24}
        style={{
          width: '100%',
          flex: 'none',
        }}
      >
        <Button
          size="md"
          fullWidth
          onClick={() => {
            setStateTab('state-path-info');
          }}
        >
          {tBookingActions('actions.confirm')}
        </Button>
      </Box>
    </Box>
  );
};

const AddPackageType = ({
  form,
  trends,
  handleSelectItem,
  pathIndex,
  setStateTab,
}) => {
  const tBooking = useTranslations('booking');
  const [keyWord, setKeyword] = useState('');

  const [debounced] = useDebouncedValue(keyWord, 500);
  const isMobile = useIsMobile();
  const { data: suggestionTrends, isLoading: isLoadingSuggestion } =
    useGetSuggestionItemTrend({ query: debounced });
  return (
    <Stack>
      <Box py="sm" px="md">
        {/* <TextInput
          leftSection={<Search className="size-6" />}
          placeholder="Nhập loại hàng hoá"
          onChange={(e) => setKeyword(e.target.value)}
        /> */}
        <FloatingLabelInput
          label={'Nhập loại hàng hoá'}
          onChange={(e) => setKeyword(e.target.value)}
          h="auto"
          leftSection={<Search width={24} height={24} />}
        />
      </Box>
      <Box
        style={{
          height: isMobile ? '100%' : 410,
          overflowY: 'auto',
        }}
      >
        <Stack mb={24} gap={0}>
          <Text px="md" tt="uppercase">
            {tBooking('special_requests.recomend_package_info_type_for_you')}
          </Text>
          {isLoadingSuggestion ? <LoadingOverlay /> : null}
          {suggestionTrends?.map((item, idx) => {
            return (
              <>
                {idx > 0 ? <Divider mx="md" /> : null}

                <Button
                  key={item.code}
                  py={12}
                  mih={64}
                  fullWidth
                  radius={0}
                  // disabled={form
                  //   .watch(`path.${pathIndex}.item_descriptions`)
                  //   ?.map(({ keyword }) => keyword)
                  //   ?.includes(item.keyword)}
                  variant="white"
                  justify="flex-start"
                  className={cn('hover:!bg-neutral-20', {
                    '!text-60 !bg-primary-40 hover:!bg-primary-40': form
                      .watch(`path.${pathIndex}.item_descriptions`)
                      ?.map(({ keyword }) => keyword)
                      ?.includes(item.keyword),
                  })}
                  onClick={() => {
                    handleSelectItem(item.keyword, pathIndex, item);
                    setStateTab('item-info');
                  }}
                  styles={{
                    root: {
                      pointerEvents: form
                        .watch(`path.${pathIndex}.item_descriptions`)
                        ?.map(({ keyword }) => keyword)
                        ?.includes(item.keyword)
                        ? 'none'
                        : 'auto',
                      '&:hover': {
                        backgroundColor: 'var(--mantine-color-neutral-4)',
                      },
                    },
                    label: {
                      color: 'var(--mantine-color-neutral-6)',
                    },
                  }}
                >
                  {item.keyword}
                </Button>
              </>
            );
          })}
          {suggestionTrends && suggestionTrends.length <= 0
            ? trends?.map((trend, idx) => (
                <>
                  {idx > 0 ? <Divider mx="md" /> : null}
                  <Button
                    py={12}
                    mih={64}
                    key={trend.code}
                    fullWidth
                    // disabled={form
                    //   .watch(`path.${pathIndex}.item_descriptions`)
                    //   ?.map(({ keyword }) => keyword)
                    //   ?.includes(trend.keyword)}
                    radius={0}
                    variant="white"
                    className={cn('hover:!bg-neutral-20', {
                      '!bg-primary-40 !text-neutral-60 hover:!bg-primary-40':
                        form
                          .watch(`path.${pathIndex}.item_descriptions`)
                          ?.map(({ keyword }) => keyword)
                          ?.includes(trend.keyword),
                    })}
                    justify="flex-start"
                    onClick={() => {
                      handleSelectItem(trend.keyword, pathIndex);
                      setStateTab('item-info');
                    }}
                    styles={{
                      root: {
                        pointerEvents: form
                          .watch(`path.${pathIndex}.item_descriptions`)
                          ?.map(({ keyword }) => keyword)
                          ?.includes(trend.keyword)
                          ? 'none'
                          : 'auto',
                        '&:hover': {
                          backgroundColor: 'var(--mantine-color-neutral-4)',
                        },
                      },
                      label: {
                        color: 'var(--mantine-color-neutral-6)',
                      },
                    }}
                  >
                    {trend.keyword}
                  </Button>
                </>
              ))
            : null}
        </Stack>
      </Box>
    </Stack>
  );
};
export default InsuranceItemModal;
